/**
 * Prijsboek-flow: 1) verfijn catalogus → 2) kies bouwdeel → variant + aantal + cluster → 3) toevoegen.
 * Lijst-UX gelijk aan referentiemateriaalpaneel (debounce, tabel, catalogusversie).
 */
import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus,
  Trash2,
  Search,
  ChevronUp,
  ChevronDown,
  Undo2,
  Redo2,
  BookOpen,
  Tags,
} from 'lucide-react';
import { api } from '../lib/api';
import { formatSystemOptionLabel, formatVariantOptionLabel, titleCaseFromSlug } from '../lib/catalog-labels';
import { Tooltip } from './ui/Tooltip';

interface System {
  system_id: string;
  label: string;
  unit: string;
  discipline?: string;
  group_code?: string;
  catalog_subgroup?: string;
}

interface Variant {
  variant_id: string;
  label: string;
}

export interface SystemSelection {
  /** Stabiele sleutel voor React / volgorde */
  line_id: string;
  system_id: string;
  variant_id: string;
  quantity: number;
  /** Cluster: bijv. "2e verd › App 12 › Keuken" of "Woning A — Badkamer" */
  building_part?: string;
  floor?: string;
  room_number?: string;
}

function formatCatalogUnit(unit: string): string {
  if (!unit || unit === '—') return '—';
  if (unit === 'm2') return 'm²';
  if (unit === 'm3') return 'm³';
  if (unit === 'm1') return 'm';
  return unit;
}

function newLineId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `line_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export interface ManualSystemPickerProps {
  value: SystemSelection[];
  onChange: (systems: SystemSelection[]) => void;
  catalogProjectType?: string;
  catalogBuildingType?: string;
  projectId?: string;
  tenantId?: string;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export function ManualSystemPicker({
  value,
  onChange,
  catalogProjectType,
  catalogBuildingType,
  projectId,
  tenantId = 'default',
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
}: ManualSystemPickerProps) {
  const [systems, setSystems] = useState<System[]>([]);
  /** Laatste succesvolle zoekresultaten — voor compacte bouwdeel-dropdown per regel */
  const [lastCatalogHits, setLastCatalogHits] = useState<System[]>([]);
  const [variantsBySystem, setVariantsBySystem] = useState<Record<string, Variant[]>>({});
  const [libraryGroups, setLibraryGroups] = useState<Array<{ group_code: string; label_nl: string }>>([]);
  const [subgroups, setSubgroups] = useState<Array<{ code: string; label_nl: string }>>([]);
  const [groupCode, setGroupCode] = useState('');
  const [catalogSubgroup, setCatalogSubgroup] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogVersion, setCatalogVersion] = useState<string | null>(null);
  /** Totaal actieve systemen/varianten uit GET /catalog/meta (Fase 7 — hele bibliotheek, niet zoekfilter). */
  const [libraryTotals, setLibraryTotals] = useState<{
    systems: number;
    variants: number;
    version: string | null;
  } | null>(null);
  const prevGroupRef = useRef<string>('');

  const [pickedSystem, setPickedSystem] = useState<System | null>(null);
  const [composerVariantId, setComposerVariantId] = useState('');
  const [composerQty, setComposerQty] = useState(1);
  const [composerBuildingPart, setComposerBuildingPart] = useState('');
  const [clusterRenameOpen, setClusterRenameOpen] = useState(false);
  const [clusterRenameFrom, setClusterRenameFrom] = useState('');
  const [clusterRenameTo, setClusterRenameTo] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 320);
    return () => clearTimeout(t);
  }, [searchInput]);

  const hasProjectContext = Boolean(projectId) || Boolean(catalogProjectType && catalogBuildingType);
  const hasNarrowing =
    (groupCode.length === 2 && /^\d{2}$/.test(groupCode)) || debouncedSearch.length >= 2;

  const refreshSystems = useCallback(async () => {
    if (!hasProjectContext || !hasNarrowing) {
      setSystems([]);
      setCatalogError(null);
      return;
    }
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const r = await api.getSystems({
        project_id: projectId,
        project_type: catalogProjectType,
        building_type: catalogBuildingType,
        group_code: groupCode && /^\d{2}$/.test(groupCode) ? groupCode : undefined,
        q: debouncedSearch.length >= 2 ? debouncedSearch : undefined,
        catalog_subgroup: catalogSubgroup || undefined,
        tenant_id: tenantId,
      });
      const list = r.systems.length > 0 ? r.systems : [];
      setSystems(list);
      setLastCatalogHits(list);
      setCatalogVersion(r.catalog_version ?? null);
    } catch (e) {
      setSystems([]);
      setCatalogVersion(null);
      setCatalogError(e instanceof Error ? e.message : 'Catalogus kon niet geladen worden');
    } finally {
      setLoadingCatalog(false);
    }
  }, [
    hasProjectContext,
    hasNarrowing,
    projectId,
    catalogProjectType,
    catalogBuildingType,
    groupCode,
    debouncedSearch,
    catalogSubgroup,
    tenantId,
  ]);

  useEffect(() => {
    const prev = prevGroupRef.current;
    if (prev !== '' && prev !== groupCode) {
      setCatalogSubgroup('');
    }
    prevGroupRef.current = groupCode;
  }, [groupCode]);

  useEffect(() => {
    if (!groupCode || !/^\d{2}$/.test(groupCode)) {
      setSubgroups([]);
      return;
    }
    api
      .getLibrarySubgroups(groupCode)
      .then((r) => setSubgroups(r.subgroups ?? []))
      .catch(() => setSubgroups([]));
  }, [groupCode]);

  useEffect(() => {
    api
      .getLibraryGroups()
      .then((r) => setLibraryGroups((r.groups ?? []).map((g) => ({ group_code: g.group_code, label_nl: g.label_nl }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getCatalogMeta(tenantId)
      .then((r) => {
        if (cancelled) return;
        const c = r.counts;
        if (c && typeof c.systems_active === 'number' && typeof c.variants_active === 'number') {
          setLibraryTotals({
            systems: c.systems_active,
            variants: c.variants_active,
            version: r.catalog_version ?? null,
          });
        } else {
          setLibraryTotals(null);
        }
      })
      .catch(() => {
        if (!cancelled) setLibraryTotals(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    void refreshSystems();
  }, [refreshSystems]);

  const loadVariants = useCallback((systemId: string) => {
    if (!systemId) return;
    api
      .getSystemVariants(systemId, tenantId)
      .then((r) => setVariantsBySystem((prev) => ({ ...prev, [systemId]: r.variants })))
      .catch(() => {});
  }, [tenantId]);

  /** Voorkomt lege variant-dropdowns na navigatie: alle system_ids in de BOQ vooraf laden. */
  const boqSystemIdsKey = useMemo(
    () => [...new Set(value.map((r) => r.system_id))].sort().join('|'),
    [value]
  );
  useEffect(() => {
    if (!boqSystemIdsKey) return;
    const ids = boqSystemIdsKey.split('|').filter(Boolean);
    let cancelled = false;
    void Promise.all(
      ids.map((sid) =>
        api.getSystemVariants(sid, tenantId).then((r) => {
          if (!cancelled) {
            setVariantsBySystem((prev) => ({ ...prev, [sid]: r.variants }));
          }
        })
      )
    );
    return () => {
      cancelled = true;
    };
  }, [boqSystemIdsKey, tenantId]);

  useEffect(() => {
    if (!pickedSystem) {
      setComposerVariantId('');
      return;
    }
    const sid = pickedSystem.system_id;
    setComposerVariantId('');
    api
      .getSystemVariants(sid, tenantId)
      .then((r) => {
        const vars = r.variants;
        setVariantsBySystem((prev) => ({ ...prev, [sid]: vars }));
        setComposerVariantId(vars[0]?.variant_id ?? '');
      })
      .catch(() => {});
  }, [pickedSystem, tenantId]);

  const addFromComposer = () => {
    if (!pickedSystem) return;
    const vars = variantsBySystem[pickedSystem.system_id] ?? [];
    const vid =
      composerVariantId ||
      vars[0]?.variant_id ||
      `${pickedSystem.system_id}_default`;
    const qty = Number(composerQty);
    if (!Number.isFinite(qty) || qty <= 0) return;
    onChange([
      ...value,
      {
        line_id: newLineId(),
        system_id: pickedSystem.system_id,
        variant_id: vid,
        quantity: qty,
        building_part: composerBuildingPart.trim() || undefined,
      },
    ]);
  };

  const composerVars = pickedSystem ? variantsBySystem[pickedSystem.system_id] : undefined;
  const canCompose =
    Boolean(pickedSystem) &&
    Boolean(composerVariantId || composerVars?.[0]?.variant_id) &&
    composerQty > 0;

  const updateRow = (index: number, updates: Partial<SystemSelection>) => {
    const next = [...value];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, ...updates };
    if (updates.system_id && updates.system_id !== cur.system_id) {
      loadVariants(updates.system_id);
      const vars = variantsBySystem[updates.system_id];
      next[index]!.variant_id = vars?.[0]?.variant_id ?? `${updates.system_id}_default`;
    }
    onChange(next);
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const moveRow = (index: number, delta: number) => {
    const j = index + delta;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    const tmp = next[index]!;
    next[index] = next[j]!;
    next[j] = tmp;
    onChange(next);
  };

  const rowSystemOptions = useCallback(
    (row: SystemSelection) => {
      const m = new Map<string, System>();
      for (const s of lastCatalogHits) {
        m.set(s.system_id, s);
      }
      if (!m.has(row.system_id)) {
        m.set(row.system_id, {
          system_id: row.system_id,
          label: titleCaseFromSlug(row.system_id),
          unit: '—',
        });
      }
      return [...m.values()];
    },
    [lastCatalogHits]
  );

  const clusterRenameOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const r of value) {
      keys.add((r.building_part ?? '').trim());
    }
    return [...keys].sort((a, b) => {
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b, 'nl');
    });
  }, [value]);

  const applyClusterRename = () => {
    const fromKey = clusterRenameFrom;
    const newName = clusterRenameTo.trim();
    const fromEmpty = fromKey === '__EMPTY__';
    const matchVal = fromEmpty ? '' : fromKey;
    onChange(
      value.map((r) => {
        const t = (r.building_part ?? '').trim();
        if (fromEmpty ? t === '' : t === matchVal) {
          return { ...r, building_part: newName };
        }
        return r;
      })
    );
    setClusterRenameOpen(false);
    setClusterRenameTo('');
  };

  const hitCount = systems.length;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="mb-3 flex items-start gap-2">
          <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div>
            <p className="text-sm font-medium text-text">Stap 1 — Prijsboek doorzoeken</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Kies een <strong>hoofdgroep</strong>, optioneel een <strong>subgroep</strong> (bijv. wanden/vloeren), en/of
              typ minstens <strong>2 tekens</strong> om te zoeken. Klik een bouwdeel in de tabel — daarna stap 2.
            </p>
            {(libraryTotals || catalogVersion) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
                {libraryTotals && (
                  <span className="inline-flex items-center gap-1">
                    <span>
                      Bibliotheek (actief):{' '}
                      <strong className="font-medium text-text">{libraryTotals.systems}</strong> systemen ·{' '}
                      <strong className="font-medium text-text">{libraryTotals.variants}</strong> varianten
                    </span>
                    <Tooltip
                      iconOnly
                      label="Uitleg totalen"
                      content="Totaal in jouw tenant (seed + eventuele D1-merge). Dit is niet het aantal treffers van je huidige zoekopdracht (max. 50 per zoekactie)."
                    />
                  </span>
                )}
                {(libraryTotals?.version || catalogVersion) && (
                  <span className="text-text-muted/90">
                    {libraryTotals ? '· ' : null}
                    bundel <span className="font-mono">{libraryTotals?.version || catalogVersion}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {!hasProjectContext && (
          <p className="mb-3 text-xs text-amber-800 dark:text-amber-200">
            Stel projecttype en gebouwtype in om het prijsboek te gebruiken.
          </p>
        )}
        <div className="mb-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 flex items-center gap-1 text-xs text-text-muted">
              Hoofdgroep
              <Tooltip
                iconOnly
                label="Hoofdgroep"
                content="Tweecijferige library-hoofdgroep. Vereist voor subgroep-filter."
              />
            </label>
            <select
              value={groupCode}
              onChange={(e) => setGroupCode(e.target.value)}
              className="w-full rounded-lg border border-border px-2 py-2 text-sm"
            >
              <option value="">— kies —</option>
              {libraryGroups.map((g) => (
                <option key={g.group_code} value={g.group_code}>
                  {g.group_code} — {g.label_nl}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1 text-xs text-text-muted">
              Subgroep (optioneel)
              <Tooltip
                iconOnly
                label="Subgroep"
                content="Verfijnt binnen de gekozen hoofdgroep, bijv. Wanden of Vloeren bij Afbouw."
              />
            </label>
            <select
              value={catalogSubgroup}
              onChange={(e) => setCatalogSubgroup(e.target.value)}
              disabled={!groupCode || subgroups.length === 0}
              className="w-full rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50"
            >
              <option value="">— alle subgroepen —</option>
              {subgroups.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label_nl}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Zoek op naam (min. 2 tekens)…"
            className="w-full rounded-lg border border-border py-2 pl-9 pr-3 text-sm text-text"
          />
        </div>
        {catalogError && <p className="mb-2 text-xs text-red-600">{catalogError}</p>}
        {loadingCatalog && <p className="text-xs text-text-muted">Laden…</p>}
        {!loadingCatalog && !catalogError && hasProjectContext && hasNarrowing && (
          <p className="mb-2 text-xs text-text-muted">
            {hitCount} treffer{hitCount === 1 ? '' : 's'} — klik een rij om door te gaan naar stap 2
          </p>
        )}
        {hasProjectContext && hasNarrowing && !loadingCatalog && systems.length > 0 && (
          <div className="max-h-72 overflow-auto rounded-md border border-border bg-surface-card">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-surface-card">
                <tr className="border-b border-border text-text-muted">
                  <th className="px-2 py-2 font-medium">Bouwdeel</th>
                  <th className="px-2 py-2 font-medium">Eenheid</th>
                </tr>
              </thead>
              <tbody>
                {systems.map((s) => (
                  <tr
                    key={s.system_id}
                    className={`cursor-pointer border-b border-border/60 transition hover:bg-primary/10 ${
                      pickedSystem?.system_id === s.system_id ? 'bg-primary/15' : ''
                    }`}
                    onClick={() => setPickedSystem(s)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPickedSystem(s);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <td className="px-2 py-2">
                      <span className="font-medium text-text">{s.label}</span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-text-muted">{formatCatalogUnit(s.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasProjectContext && hasNarrowing && !loadingCatalog && systems.length === 0 && !catalogError && (
          <p className="text-xs text-text-muted">Geen resultaten; pas zoekterm, subgroep of groep aan.</p>
        )}
      </div>

      <div className="rounded-lg border border-dashed border-primary/40 bg-surface-card/80 p-4">
        <p className="mb-3 text-sm font-medium text-text">Stap 2 — Variant, aantal & groepering</p>
        {!pickedSystem ? (
          <p className="text-sm text-text-muted">Selecteer eerst een bouwdeel uit de tabel hierboven.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-text-muted">
              Gekozen: <strong className="text-text">{pickedSystem.label}</strong>
              <span className="ml-1 text-text-muted">({formatCatalogUnit(pickedSystem.unit)})</span>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-text-muted">Variant</label>
                <select
                  value={composerVariantId}
                  onChange={(e) => setComposerVariantId(e.target.value)}
                  className="w-full rounded border border-border px-2 py-2 text-sm"
                >
                  {(variantsBySystem[pickedSystem.system_id] ?? []).map((v) => (
                    <option key={v.variant_id} value={v.variant_id}>
                      {v.label}
                    </option>
                  ))}
                  {!variantsBySystem[pickedSystem.system_id]?.length && (
                    <option value={composerVariantId}>{composerVariantId || 'Laden…'}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-text-muted">Aantal</label>
                <input
                  type="number"
                  min={0.01}
                  step="0.01"
                  value={composerQty}
                  onChange={(e) => setComposerQty(Number(e.target.value) || 0)}
                  className="w-full rounded border border-border px-2 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs text-text-muted">
                Bouwcluster — waar hoort deze post bij?
                <Tooltip
                  iconOnly
                  label="Groepering"
                  content="Vrije naam om posten te bundelen in het resultaat (verdieping, appartement, ruimte). Zelfde tekst = zelfde cluster."
                />
              </label>
              <input
                type="text"
                value={composerBuildingPart}
                onChange={(e) => setComposerBuildingPart(e.target.value)}
                placeholder="Bijv. 2e verdieping › App 12 › Keuken — of laat leeg voor ‘algemeen’"
                className="w-full rounded border border-border px-2 py-2 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => addFromComposer()}
              disabled={!canCompose}
              className="inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Toevoegen aan begroting
            </button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-dashed border-border bg-surface/60 px-3 py-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-text">Stap 3 — Je begrotingsposten ({value.length})</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Pas hier cluster, verdieping, ruimte en bouwdeel aan. Sla onderaan op om door te kunnen naar Resultaat.
            </p>
          </div>
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const opts = clusterRenameOptions;
                setClusterRenameFrom(opts.includes('') ? '__EMPTY__' : opts.filter(Boolean)[0] ?? '');
                setClusterRenameTo('');
                setClusterRenameOpen(true);
              }}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-text hover:bg-surface"
            >
              <Tags className="h-3.5 w-3.5" aria-hidden />
              Cluster hernoemen (bulk)
            </button>
          )}
        </div>
      </div>

      {clusterRenameOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cluster-rename-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Sluiten"
            onClick={() => setClusterRenameOpen(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-surface-card p-5 shadow-xl">
            <h3 id="cluster-rename-title" className="text-base font-semibold text-text">
              Bouwcluster hernoemen
            </h3>
            <p className="mt-1 text-xs text-text-muted">
              Alle regels met de gekozen cluster krijgen dezelfde nieuwe naam — handig voor portefeuilles en complexen.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-text">Bestaande cluster</label>
                <select
                  className="w-full rounded-lg border border-border px-2 py-2 text-sm"
                  value={clusterRenameFrom}
                  onChange={(e) => setClusterRenameFrom(e.target.value)}
                >
                  {clusterRenameOptions.includes('') && (
                    <option value="__EMPTY__">Zonder cluster (leeg)</option>
                  )}
                  {clusterRenameOptions
                    .filter((k) => k !== '')
                    .map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-text">Nieuwe naam</label>
                <input
                  type="text"
                  value={clusterRenameTo}
                  onChange={(e) => setClusterRenameTo(e.target.value)}
                  placeholder="Bijv. 2e verdieping › App 12"
                  className="w-full rounded-lg border border-border px-2 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-border px-3 py-2 text-sm" onClick={() => setClusterRenameOpen(false)}>
                Annuleren
              </button>
              <button
                type="button"
                disabled={!clusterRenameTo.trim()}
                onClick={applyClusterRename}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Toepassen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-text">Volgorde & acties</p>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => onUndo?.()}
            disabled={!canUndo}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-card px-2 py-1.5 text-xs font-medium disabled:opacity-40"
            title="Ongedaan maken"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </button>
          <button
            type="button"
            onClick={() => onRedo?.()}
            disabled={!canRedo}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-card px-2 py-1.5 text-xs font-medium disabled:opacity-40"
            title="Opnieuw"
          >
            <Redo2 className="h-3.5 w-3.5" />
            Redo
          </button>
        </div>
      </div>

      {value.length === 0 && (
        <p className="rounded-lg border border-dashed border-border bg-surface p-4 text-center text-sm text-text-muted">
          Nog geen regels. Doorloop stap 1 en 2 om posten toe te voegen.
        </p>
      )}

      <p className="text-xs text-text-muted">
        Per regel: bouwdeel-dropdown toont alleen <strong>dit bouwdeel</strong> plus de laatste prijsboek-treffers. Voor
        een ander bouwdeel: zoek in stap 1 en voeg opnieuw toe.
      </p>

      {value.map((row, i) => {
        const rk = (row.building_part ?? '').trim() || '__ungrouped__';
        const prevRk =
          i > 0 ? (value[i - 1]!.building_part ?? '').trim() || '__ungrouped__' : null;
        const showClusterHeader = i === 0 || rk !== prevRk;
        const opts = rowSystemOptions(row);
        const vars = variantsBySystem[row.system_id] ?? [];
        const idsInList = new Set(vars.map((v) => v.variant_id));
        let variantList: Variant[] =
          vars.length > 0
            ? vars
            : row.variant_id
              ? [{ variant_id: row.variant_id, label: formatVariantOptionLabel(row.variant_id, undefined) }]
              : [];
        if (vars.length > 0 && row.variant_id && !idsInList.has(row.variant_id)) {
          variantList = [
            ...vars,
            { variant_id: row.variant_id, label: formatVariantOptionLabel(row.variant_id, undefined) },
          ];
        }
        return (
          <Fragment key={row.line_id || `row-${i}`}>
            {showClusterHeader ? (
              <h4 className="border-b border-border pb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                {rk === '__ungrouped__' ? 'Zonder bouwcluster' : rk}
              </h4>
            ) : null}
            <div
              id={row.line_id ? `boq-line-${row.line_id}` : undefined}
              className="flex flex-col gap-3 rounded-lg border border-border bg-surface-card p-3 scroll-mt-24 sm:flex-row sm:flex-wrap sm:items-end"
            >
                <div className="flex shrink-0 flex-col gap-1">
                  <button
                    type="button"
                    aria-label="Omhoog"
                    disabled={i === 0}
                    onClick={() => moveRow(i, -1)}
                    className="rounded border border-border p-1 hover:bg-surface disabled:opacity-30"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Omlaag"
                    disabled={i >= value.length - 1}
                    onClick={() => moveRow(i, 1)}
                    className="rounded border border-border p-1 hover:bg-surface disabled:opacity-30"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <div className="min-w-[120px] flex-1 sm:max-w-[200px]">
                  <label className="mb-1 block text-xs text-text-muted">Bouwcluster</label>
                  <input
                    type="text"
                    value={row.building_part ?? ''}
                    onChange={(e) => updateRow(i, { building_part: e.target.value })}
                    placeholder="Zelfde naam = zelfde groep in resultaat"
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="min-w-[72px] w-24">
                  <label className="mb-1 block text-xs text-text-muted">Verdieping</label>
                  <input
                    type="text"
                    value={row.floor ?? ''}
                    onChange={(e) => updateRow(i, { floor: e.target.value })}
                    placeholder="—"
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="min-w-[88px] w-28">
                  <label className="mb-1 block text-xs text-text-muted">Ruimte nr.</label>
                  <input
                    type="text"
                    value={row.room_number ?? ''}
                    onChange={(e) => updateRow(i, { room_number: e.target.value })}
                    placeholder="—"
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="min-w-[200px] flex-1">
                  <label className="mb-1 block text-xs text-text-muted">Bouwdeel</label>
                  <select
                    value={row.system_id}
                    onChange={(e) => updateRow(i, { system_id: e.target.value })}
                    onFocus={() => loadVariants(row.system_id)}
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  >
                    {opts.map((s) => (
                      <option key={s.system_id} value={s.system_id}>
                        {formatSystemOptionLabel(s.system_id, s.label, s.unit)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[180px] flex-1">
                  <label className="mb-1 block text-xs text-text-muted">Variant</label>
                  <select
                    value={row.variant_id}
                    onChange={(e) => updateRow(i, { variant_id: e.target.value })}
                    onFocus={() => loadVariants(row.system_id)}
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  >
                    {variantList.map((v) => (
                      <option key={v.variant_id} value={v.variant_id}>
                        {formatVariantOptionLabel(v.variant_id, v.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  <label className="mb-1 block text-xs text-text-muted">Aantal</label>
                  <input
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={row.quantity}
                    onChange={(e) => updateRow(i, { quantity: Number(e.target.value) || 0 })}
                    className="w-full rounded border border-border px-2 py-1.5 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="rounded p-1.5 text-red-600 hover:bg-red-50"
                  aria-label="Verwijderen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
          </Fragment>
        );
      })}
    </div>
  );
}
